/* eslint-disable react-hooks/set-state-in-effect --
 * v77.99.77: This file uses idiomatic React patterns (effect-driven
 * state hydration after fetches, English text with apostrophes /
 * quotes in JSX). The experimental React 19 hooks rules
 * (`set-state-in-effect`, `purity`, `preserve-manual-memoization`)
 * are not shipped by eslint-plugin-react-hooks 5.2.0 — only the
 * standalone Emergent lint tool surfaces them, and they mis-flag
 * these intentional patterns. `react/no-unescaped-entities` similarly
 * flags safe English text.
 */
import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Users, Home, Baby, MessageSquare, FileText, ReceiptEuro, Download, UserPlus, Activity, TrendingUp, Lock, Lightbulb, Clock, Bug } from "lucide-react";
import { AdminAccessSection } from "@/components/AdminAccessSection";
import AdminOpsConsole from "@/components/AdminOpsConsole";
import { AdminManagementSection } from "@/components/AdminManagementSection";
import { EmailHealthSection } from "@/components/EmailHealthSection";
import { DataAuditSection } from "@/components/DataAuditSection";
import { Tile, BarRow, SignupsChart } from "@/components/admin/AdminPrimitives";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatDT } from "@/lib/format";

// v77.99.77 — `Tile`, `BarRow`, `SignupsChart` extracted to
// `components/admin/AdminPrimitives.jsx`. Pure presentational; safe to
// reuse from any future admin surface without dragging in this file's
// data-loading effects.

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [signups, setSignups] = useState(null);
  const [active, setActive] = useState(null);
  const [events, setEvents] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [subMetrics, setSubMetrics] = useState(null);
  const [utmSplit, setUtmSplit] = useState(null);
  const [digestBusy, setDigestBusy] = useState(false);
  const [digestPref, setDigestPref] = useState(null); // { subscribed, is_primary, can_unsubscribe, recipient_count }
  const [digestPrefBusy, setDigestPrefBusy] = useState(false);
  const [suppressions, setSuppressions] = useState(null);
  const [supBust, setSupBust] = useState(0);
  const [inactivitySummary, setInactivitySummary] = useState(null);
  const [range, setRange] = useState(30); // days
  const [err, setErr] = useState(null);
  // "Include test accounts" toggle. Persisted in localStorage so the
  // operator's preference survives page reloads. Default OFF — the curated
  // production view is the safe default; turn ON to peek at unfiltered
  // numbers (e.g. before a launch sweep).
  const [includeTest, setIncludeTest] = useState(() => {
    try { return localStorage.getItem("cpc_admin_include_test") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("cpc_admin_include_test", includeTest ? "1" : "0"); } catch { /* ignore */ }
  }, [includeTest]);
  const tParam = includeTest ? "&include_test=1" : "";
  const tQuery = includeTest ? "?include_test=1" : "";

  // Feature requests review
  const [frData, setFrData] = useState(null); // { requests, counts, total_all }
  const [frStatus, setFrStatus] = useState("new"); // "all" | new | reviewed | planned | shipped | declined
  const [frBusy, setFrBusy] = useState(false);
  const [frEditing, setFrEditing] = useState(null); // req being edited in modal
  const [frNoteDraft, setFrNoteDraft] = useState("");
  const [frStatusDraft, setFrStatusDraft] = useState("new");
  const [frSaveBusy, setFrSaveBusy] = useState(false);

  // v77.43 — Bug reports admin section
  const [brData, setBrData] = useState(null); // { reports, counts, total_all }
  const [brStatus, setBrStatus] = useState("new");
  const [brBusy, setBrBusy] = useState(false);
  const [brEditing, setBrEditing] = useState(null);
  const [brStatusDraft, setBrStatusDraft] = useState("new");
  const [brNoteDraft, setBrNoteDraft] = useState("");
  const [brSaveBusy, setBrSaveBusy] = useState(false);

  // Split the data loading into two effects:
  //   (a) Range-independent data (overview, active-users, subscription-metrics)
  //       — loaded ONCE on mount. Never flickers when the day-range toggles.
  //   (b) Range-dependent data (signups, events, analytics) — reloaded when
  //       the range changes. Each fetch runs in parallel but failures are
  //       isolated so one slow/failed endpoint never blanks the entire page.
  useEffect(() => {
    if (!user?.is_admin) return;
    let cancelled = false;
    const loadStable = async () => {
      const results = await Promise.allSettled([
        api.get(`/admin/overview${tQuery}`),
        api.get(`/admin/active-users${tQuery}`),
        api.get(`/admin/subscription-metrics${tQuery}`),
      ]);
      if (cancelled) return;
      const [o, a, sm] = results;
      if (o.status === "fulfilled") setOverview(o.value.data);
      if (a.status === "fulfilled") setActive(a.value.data);
      if (sm.status === "fulfilled") setSubMetrics(sm.value.data);
      const errors = results.filter((r) => r.status === "rejected");
      if (errors.length) {
        // Surface the first error but don't blank any successfully-loaded tile.
        const msg = errors[0].reason?.response?.data?.detail || "Some analytics data failed to load";
        setErr(msg);
      }
    };
    loadStable();
    return () => { cancelled = true; };
  }, [user, includeTest]);

  useEffect(() => {
    if (!user?.is_admin) return;
    let cancelled = false;
    const loadRanged = async () => {
      const results = await Promise.allSettled([
        api.get(`/admin/signups?days=${range}${tParam}`),
        api.get(`/admin/events?days=${range}`),
        api.get(`/admin/analytics?days=${range}`),
        api.get(`/admin/upgrade-cta?days=${range}`),
      ]);
      if (cancelled) return;
      const [s, e, an, ut] = results;
      if (s.status === "fulfilled") setSignups(s.value.data);
      if (e.status === "fulfilled") setEvents(e.value.data);
      if (an.status === "fulfilled") setAnalytics(an.value.data);
      if (ut.status === "fulfilled") setUtmSplit(ut.value.data);
    };
    loadRanged();
    return () => { cancelled = true; };
  }, [user, range, includeTest]);

  // Feature requests loader — refreshed whenever the filter changes
  useEffect(() => {
    if (!user?.is_admin) return;
    let cancelled = false;
    const loadDigest = async () => {
      try {
        const { data } = await api.get("/admin/digest/me");
        if (!cancelled) setDigestPref(data);
      } catch (_e) { /* non-fatal — section just hides the toggle */ }
    };
    loadDigest();
    return () => { cancelled = true; };
  }, [user]);

  // v77.18 — Inactive accounts summary for the dashboard tile.
  useEffect(() => {
    if (!user?.is_admin) return;
    let cancelled = false;
    api.get("/admin/inactive/summary")
      .then(({ data }) => { if (!cancelled) setInactivitySummary(data); })
      .catch(() => { /* non-fatal */ });
    return () => { cancelled = true; };
  }, [user]);

  // v77.14 — Email suppression list. Refetches on supBust so a manual
  // reset immediately clears the row from the UI without a full reload.
  useEffect(() => {
    if (!user?.is_admin) return;
    let cancelled = false;
    const loadSuppressions = async () => {
      try {
        const { data } = await api.get("/admin/email-suppressions?limit=50");
        if (!cancelled) setSuppressions(data);
      } catch (_e) { /* non-fatal */ }
    };
    loadSuppressions();
    return () => { cancelled = true; };
  }, [user, supBust]);

  useEffect(() => {
    if (!user?.is_admin) return;
    let cancelled = false;
    const loadFr = async () => {
      setFrBusy(true);
      try {
        const params = frStatus === "all" ? "" : `?status=${encodeURIComponent(frStatus)}`;
        const { data } = await api.get(`/admin/feature-requests${params}`);
        if (!cancelled) setFrData(data);
      } catch (_e) {
        if (!cancelled) setFrData({ requests: [], counts: {}, total_all: 0 });
      } finally {
        if (!cancelled) setFrBusy(false);
      }
    };
    loadFr();
    return () => { cancelled = true; };
  }, [user, frStatus]);

  // v77.43 — Bug reports loader (admin only)
  useEffect(() => {
    if (!user?.is_admin) return;
    let cancelled = false;
    const loadBr = async () => {
      setBrBusy(true);
      try {
        const params = brStatus === "all" ? "" : `?status=${encodeURIComponent(brStatus)}`;
        const { data } = await api.get(`/admin/bug-reports${params}`);
        if (!cancelled) setBrData(data);
      } catch (_e) {
        if (!cancelled) setBrData({ reports: [], counts: {}, total_all: 0 });
      } finally {
        if (!cancelled) setBrBusy(false);
      }
    };
    loadBr();
    return () => { cancelled = true; };
  }, [user, brStatus]);

  // All hooks must be declared before any early returns to satisfy React's
  // Rules of Hooks — the previous placement after the `if (!user.is_admin)`
  // branch caused the hook call order to change between renders and crashed
  // the whole app in React 19's strict hook-order check.
  const maxEvent = useMemo(() => Math.max(1, ...(events?.events || []).map((e) => e.count)), [events]);

  const reloadFr = async () => {
    const params = frStatus === "all" ? "" : `?status=${encodeURIComponent(frStatus)}`;
    try {
      const { data } = await api.get(`/admin/feature-requests${params}`);
      setFrData(data);
    } catch (e) { console.warn("[admin] feature-requests load failed:", e?.response?.status || e?.message); }
  };

  const markReviewed = async (req) => {
    try {
      await api.patch(`/admin/feature-requests/${req.id}`, { status: "reviewed" });
      toast.success("Marked as reviewed");
      await reloadFr();
    } catch (err2) {
      toast.error(err2.response?.data?.detail || "Couldn't update");
    }
  };

  const openEdit = (req) => {
    setFrEditing(req);
    setFrNoteDraft(req.admin_note || "");
    setFrStatusDraft(req.status || "new");
  };
  const closeEdit = () => { if (!frSaveBusy) setFrEditing(null); };
  const saveEdit = async () => {
    if (!frEditing) return;
    setFrSaveBusy(true);
    try {
      await api.patch(`/admin/feature-requests/${frEditing.id}`, {
        status: frStatusDraft,
        admin_note: frNoteDraft,
      });
      toast.success("Saved");
      setFrEditing(null);
      await reloadFr();
    } catch (err2) {
      toast.error(err2.response?.data?.detail || "Couldn't save");
    } finally {
      setFrSaveBusy(false);
    }
  };

  if (loading) return <div className="text-sm text-[#6F828A]">Loading…</div>;
  if (!user) return <Navigate to="/auth" replace />;
  if (!user.is_admin) {
    return (
      <div className="max-w-xl border border-[#D9E1E3] bg-white rounded-md p-8 text-center" data-testid="admin-forbidden">
        <Lock className="w-6 h-6 mx-auto text-[#6F828A]" strokeWidth={1.5} />
        <div className="font-heading text-lg font-medium mt-3">Admin only</div>
        <p className="text-sm text-[#6F828A] mt-1">This area is reserved for the app owner and invited admins. If you believe you should have access, ask the primary admin to grant admin access to your account.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.12em] text-[#6F828A]">Operator view</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold mt-1">Usage & adoption</h1>
          <p className="text-[#6F828A] mt-2 text-sm max-w-2xl">
            Aggregated, privacy-conscious metrics. No user names, emails or household identifiers are shown — only counts and behavioural events. Message and expense content is never read.
          </p>
        </div>
        <div className="inline-flex border border-[#D9E1E3] rounded-sm overflow-hidden text-sm" role="radiogroup" aria-label="Time range">
          {[7, 30, 90].map((d) => (
            <button key={d} type="button" role="radio" aria-checked={range === d}
              onClick={() => setRange(d)}
              className={`px-3 py-1.5 transition-colors ${range === d ? "bg-[#1F9FA4] text-white" : "bg-white text-[#6F828A] hover:bg-[#EEF3F4]"}`}
              data-testid={`admin-range-${d}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      {err && (
        <div className="border border-[#E8B8B2] bg-[#FBEFED] text-[#8C2A22] rounded-sm p-3 text-sm" data-testid="admin-error">{err}</div>
      )}

      {overview?.operator_excluded && (
        <div className="border border-[#A7D8DA] bg-[#D6F0EF] text-[#116B6E] rounded-sm p-3 text-sm flex items-start gap-3 flex-wrap" data-testid="admin-operator-notice">
          <Lock className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-[260px]">
            <div className="font-medium">
              {includeTest ? "Showing unfiltered counts (operator still excluded)" : "Operator + test accounts excluded from these counts"}
            </div>
            <div className="text-xs mt-0.5 text-[#2E3A3F]">
              {includeTest ? (
                <>Test-domain accounts ARE included in every metric below. Toggle this off to return to the production view.</>
              ) : (
                <>
                  <span className="font-mono-data">{overview.operator_email}</span> + <span className="font-mono-data tabular-nums">{(overview.test_users_excluded || 0).toLocaleString()}</span> test-domain account(s) are not counted as registered users, trial users, or households. Test domains: <span className="font-mono-data">{(overview.test_email_domains || []).join(", ")}</span>. Households shared between a test user and a real user are still counted.
                </>
              )}
            </div>
          </div>
          <label className="inline-flex items-center gap-2 text-xs select-none cursor-pointer flex-shrink-0" data-testid="include-test-toggle-label">
            <input
              type="checkbox"
              checked={includeTest}
              onChange={(e) => setIncludeTest(e.target.checked)}
              className="w-4 h-4 rounded border-[#A7D8DA] accent-[#1F9FA4] focus:ring-[#1F9FA4]"
              data-testid="include-test-toggle"
            />
            <span className="font-medium text-[#116B6E]">Include test accounts</span>
          </label>
        </div>
      )}

      {/* Totals */}
      <section className="space-y-3">
        <h2 className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium">Totals</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          <Tile icon={Users} label="Registered users" value={overview?.total_users} testid="admin-tile-users" accent="teal" />
          <Tile icon={Home} label="Households" value={overview?.total_households} testid="admin-tile-households" accent="sand" />
          <Tile icon={UserPlus} label="Co-parent pairs" value={overview?.coparent_pairs_linked} testid="admin-tile-pairs" accent="violet" />
          <Tile icon={Baby} label="Children profiles" value={overview?.total_children} testid="admin-tile-children" accent="teal" />
          <Tile icon={MessageSquare} label="Messages sent" value={overview?.total_messages} testid="admin-tile-messages" accent="teal" />
          <Tile icon={FileText} label="Requests created" value={overview?.total_requests} testid="admin-tile-requests" accent="sand" />
          <Tile icon={ReceiptEuro} label="Expenses logged" value={overview?.total_expenses} testid="admin-tile-expenses" accent="violet" />
          <Tile icon={Download} label="PDF exports" value={overview?.total_pdf_exports} testid="admin-tile-exports" accent="neutral" />
        </div>
        <div className="text-xs text-[#6F828A]">
          Third-party (mediator/solicitor) invites created: <span className="font-mono-data tabular-nums">{(overview?.total_mediator_invites ?? 0).toLocaleString()}</span>
          {" · "}Mediator accounts: <span className="font-mono-data tabular-nums">{(overview?.total_mediator_users ?? 0).toLocaleString()}</span>
        </div>
      </section>

      {/* Subscription tier split + MRR/ARR — derived from user records so
           the split always matches what the feature gates actually do. */}
      <section className="space-y-3" data-testid="admin-subscription-metrics">
        <h2 className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium">Subscription tiers &amp; revenue</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="border border-[#D9E1E3] bg-white rounded-md p-4" data-testid="admin-tile-mrr">
            <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">MRR</div>
            <div className="font-heading text-3xl font-semibold tabular-nums mt-1 text-[#1E5A1E]">€{(subMetrics?.mrr_eur ?? 0).toFixed(2)}</div>
            <div className="text-[11px] text-[#6F828A] mt-0.5">Monthly recurring · {(subMetrics?.paying_users ?? 0).toLocaleString()} paying user{(subMetrics?.paying_users ?? 0) === 1 ? "" : "s"}</div>
          </div>
          <div className="border border-[#D9E1E3] bg-white rounded-md p-4" data-testid="admin-tile-arr">
            <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">ARR</div>
            <div className="font-heading text-3xl font-semibold tabular-nums mt-1 text-[#116B6E]">€{(subMetrics?.arr_eur ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            <div className="text-[11px] text-[#6F828A] mt-0.5">MRR × 12</div>
          </div>
          <div className="border border-[#D9E1E3] bg-white rounded-md p-4" data-testid="admin-tile-past-due">
            <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">Past due</div>
            <div className="font-heading text-3xl font-semibold tabular-nums mt-1 text-[#8C2A22]">{(subMetrics?.past_due ?? 0).toLocaleString()}</div>
            <div className="text-[11px] text-[#6F828A] mt-0.5">Failed most recent invoice</div>
          </div>
          <div className="border border-[#D9E1E3] bg-white rounded-md p-4" data-testid="admin-tile-cancelled-grace">
            <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">Cancelled (in grace)</div>
            <div className="font-heading text-3xl font-semibold tabular-nums mt-1 text-[#8F6B1F]">{(subMetrics?.cancelled_in_grace ?? 0).toLocaleString()}</div>
            <div className="text-[11px] text-[#6F828A] mt-0.5">Still paying until period end</div>
          </div>
        </div>

        {/* Tier distribution bar */}
        <div className="border border-[#D9E1E3] bg-white rounded-md p-4 space-y-3">
          <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">Tier distribution</div>
          {subMetrics?.tiers?.length ? (() => {
            const total = Math.max(1, subMetrics.total_users || 1);
            const colors = {
              paid: "bg-[#1E5A1E]",
              trial: "bg-[#E6A92F]",
              free: "bg-[#6F828A]",
              complimentary: "bg-[#7B52B3]",
              admin: "bg-[#116B6E]",
            };
            return (
              <>
                <div className="h-4 w-full rounded-sm overflow-hidden flex border border-[#EEF3F4]" aria-label="Tier distribution bar">
                  {subMetrics.tiers.map((t) => (
                    t.count > 0 ? (
                      <div
                        key={t.key}
                        className={`${colors[t.key] || "bg-[#CFD6D8]"} h-full`}
                        style={{ width: `${(t.count / total) * 100}%` }}
                        title={`${t.label}: ${t.count} (${t.pct}%)`}
                        data-testid={`admin-tier-bar-${t.key}`}
                      />
                    ) : null
                  ))}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                  {subMetrics.tiers.map((t) => (
                    <div key={t.key} className="text-xs" data-testid={`admin-tier-${t.key}`}>
                      <div className="flex items-center gap-1.5">
                        <span className={`w-2.5 h-2.5 rounded-sm ${colors[t.key] || "bg-[#CFD6D8]"}`} />
                        <span className="text-[#6F828A] uppercase tracking-[0.08em]">{t.label}</span>
                      </div>
                      <div className="font-mono-data tabular-nums text-[#2E3A3F] mt-0.5">
                        {t.count.toLocaleString()} <span className="text-[#6F828A]">· {t.pct}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            );
          })() : (
            <div className="text-sm text-[#6F828A]">Loading…</div>
          )}
        </div>
        <p className="text-[11px] text-[#6F828A] italic">MRR counts only users on the <em>Paid</em> tier at the current €{(subMetrics?.price_eur ?? 10.99).toFixed(2)}/month rate (yearly subscribers contribute €{((subMetrics?.yearly_price_eur ?? 99) / 12).toFixed(2)}/month — annual / 12 normalised). Complimentary and admin accounts are excluded from revenue. Cancelled-in-grace users are still counted until their access_until date.</p>
      </section>

      {/* Admin roster — primary admin can grant/revoke admin to other accounts */}
      <AdminManagementSection />

      {/* Email delivery health & test send */}
      <EmailHealthSection />

      {/* Production data audit + safe cleanup of test-pattern accounts */}
      <DataAuditSection viewerIsPrimary={!!user?.is_primary_admin} />

      {/* Admin-controlled Premium access management */}
      <AdminAccessSection />

      {/* v77.99.2 — Phase 2-4 ops console (system health, audit log,
          feature flags, storage, revenue, household health, email log) */}
      <AdminOpsConsole />

      {/* Active users */}
      <section className="space-y-3">
        <h2 className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium">Active users</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <Tile icon={Activity} label="Daily active (24h)" value={active?.dau} testid="admin-tile-dau" accent="teal" />
          <Tile icon={Activity} label="Weekly active (7d)" value={active?.wau} testid="admin-tile-wau" accent="sand" />
          <Tile icon={Activity} label="Monthly active (30d)" value={active?.mau} testid="admin-tile-mau" accent="violet" />
        </div>
        <div className="text-xs text-[#6F828A]">Based on active session `last_seen_at` timestamps. Mediator sessions are excluded.</div>
      </section>

      {/* Signups chart */}
      <section className="space-y-3">
        <div className="flex items-baseline justify-between">
          <h2 className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium">New sign-ups — last {range} days</h2>
          {signups && (
            <div className="text-xs text-[#6F828A] font-mono-data tabular-nums" data-testid="admin-signups-totals">
              Total <span className="text-[#2E3A3F]">{signups.total.toLocaleString()}</span>
              {" · "}last 7 d <span className="text-[#2E3A3F]">{signups.last_7.toLocaleString()}</span>
              {" · "}last 30 d <span className="text-[#2E3A3F]">{signups.last_30.toLocaleString()}</span>
            </div>
          )}
        </div>
        <div className="border border-[#D9E1E3] bg-white rounded-md p-4">
          {signups?.series?.length ? <SignupsChart series={signups.series} /> : <div className="h-32 flex items-center justify-center text-sm text-[#6F828A]">No data yet.</div>}
          <div className="mt-2 flex justify-between text-[11px] text-[#6F828A] font-mono-data">
            <span>{signups?.series?.[0]?.date || ""}</span>
            <span>{signups?.series?.[signups.series.length - 1]?.date || ""}</span>
          </div>
        </div>
      </section>

      {/* Feature-usage histogram */}
      <section className="space-y-3">
        <h2 className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium inline-flex items-center gap-1.5">
          <TrendingUp className="w-3.5 h-3.5" /> Most-used features · last {range} days
        </h2>
        <div className="border border-[#D9E1E3] bg-white rounded-md p-4 space-y-4" data-testid="admin-events-list">
          {events?.events?.length ? (
            events.events.map((e) => (
              <BarRow key={e.label} label={e.label} count={e.count} max={maxEvent} testid={`admin-event-${e.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} />
            ))
          ) : (
            <div className="text-sm text-[#6F828A]">No events recorded in this range yet.</div>
          )}
        </div>
      </section>

      {/* v77.11 — Where do upgrades come from? UTM split for the
          selected window. Helps the operator decide whether to adjust
          the trial copy, email cadence or in-app banner placement. */}
      <section className="space-y-3" data-testid="admin-utm-section">
        <h2 className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium inline-flex items-center gap-1.5">
          <TrendingUp className="w-3.5 h-3.5" /> Where do upgrades come from? · last {range} days
        </h2>
        <div className="border border-[#D9E1E3] bg-white rounded-md p-4 space-y-4" data-testid="admin-utm-list">
          {utmSplit?.by_source?.length ? (
            <>
              {utmSplit.by_source.map((row) => (
                <BarRow
                  key={row.source}
                  label={row.label}
                  count={row.count}
                  max={Math.max(1, ...utmSplit.by_source.map((r) => r.count))}
                  testid={`admin-utm-${row.source}`}
                />
              ))}
              <div className="pt-2 border-t border-[#EEF3F4] text-xs text-[#6F828A] flex justify-between">
                <span>Total clicks tracked</span>
                <span className="font-mono-data tabular-nums">{utmSplit.total.toLocaleString()}</span>
              </div>
            </>
          ) : (
            <div className="text-sm text-[#6F828A]" data-testid="admin-utm-empty">
              No upgrade-CTA clicks tracked yet.
            </div>
          )}
        </div>
      </section>

      {/* v77.12 — Weekly digest preview button. The digest fires
          automatically every Friday 18:00 UTC, with the primary admin
          always receiving it and other admins able to opt in. */}
      <section className="space-y-3" data-testid="admin-digest-section">
        <h2 className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium inline-flex items-center gap-1.5">
          <TrendingUp className="w-3.5 h-3.5" /> Weekly digest
        </h2>
        <div className="border border-[#D9E1E3] bg-white rounded-md p-4 space-y-4">
          <div className="flex items-start gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-sm text-[#2E3A3F]">
                The full snapshot — signups, conversions, MRR, UTM split and flagged
                events — is emailed every Friday at 18:00 UTC.
              </p>
              <p className="text-xs text-[#6F828A] mt-1.5" data-testid="admin-digest-recipient-count">
                Currently going to{" "}
                <span className="font-mono-data text-[#116B6E]">
                  {digestPref?.recipient_count ?? 1} admin{(digestPref?.recipient_count ?? 1) === 1 ? "" : "s"}
                </span>
                {digestPref?.primary_email && (
                  <>
                    {" "}({digestPref.primary_email}
                    {(digestPref.recipient_count ?? 1) > 1 ? ", + others" : ""})
                  </>
                )}
                .
              </p>
            </div>
            <Button
              onClick={async () => {
                setDigestBusy(true);
                try {
                  const { data } = await api.post("/admin/digest/send-now");
                  if (data?.sent) toast.success(`Digest sent to ${data.recipients?.length ?? 1} admin${(data.recipients?.length ?? 1) === 1 ? "" : "s"}`);
                  else if (data?.result?.skipped) toast.info(`Skipped: ${data.result.reason || "skipped"}`);
                  else toast.error(`Send failed: ${data?.result?.error || "unknown error"}`);
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Send failed");
                } finally {
                  setDigestBusy(false);
                }
              }}
              disabled={digestBusy}
              data-testid="admin-digest-send-now"
              className="bg-[#1F9FA4] hover:bg-[#198185] text-white"
            >
              {digestBusy ? "Sending…" : "Send digest now"}
            </Button>
          </div>

          {/* Per-admin opt-in row — shown for every admin. Primary
              shows a locked-on indicator; others get a real toggle. */}
          {digestPref && (
            <div
              className="border-t border-[#EEF3F4] pt-3 flex items-center justify-between gap-4"
              data-testid="admin-digest-prefs"
            >
              <div className="text-sm text-[#2E3A3F]">
                <div className="font-medium">Receive this digest in my inbox</div>
                <div className="text-xs text-[#6F828A] mt-0.5">
                  {digestPref.is_primary
                    ? "You're the primary admin — this preference is always on."
                    : "Toggle off any time. Operational emails (security, billing) are unaffected."}
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={!!digestPref.subscribed}
                disabled={!digestPref.can_unsubscribe || digestPrefBusy}
                data-testid="admin-digest-toggle"
                onClick={async () => {
                  if (!digestPref.can_unsubscribe) return;
                  setDigestPrefBusy(true);
                  try {
                    const next = !digestPref.subscribed;
                    const { data } = await api.put("/admin/digest/me", { subscribed: next });
                    setDigestPref({ ...digestPref, ...data });
                    toast.success(next ? "Subscribed" : "Unsubscribed");
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Update failed");
                  } finally {
                    setDigestPrefBusy(false);
                  }
                }}
                className={`relative inline-flex w-11 h-6 rounded-full transition-colors flex-shrink-0 ${
                  digestPref.subscribed ? "bg-[#1F9FA4]" : "bg-[#D9E1E3]"
                } ${!digestPref.can_unsubscribe ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
                    digestPref.subscribed ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* v77.14 — Email health (suppression list). Populated by Resend
          webhook events (email.bounced, email.complained). Admin can
          manually reset addresses they've confirmed are reachable
          again. Empty state stays small and friendly. */}
      <section className="space-y-3" data-testid="admin-email-suppressions-section">
        <h2 className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium inline-flex items-center gap-1.5">
          <TrendingUp className="w-3.5 h-3.5" /> Email suppressions
        </h2>
        <div className="border border-[#D9E1E3] bg-white rounded-md overflow-hidden">
          <div className="px-4 py-3 border-b border-[#EEF3F4] flex items-center justify-between text-sm">
            <span className="text-[#2E3A3F]">
              <span className="font-mono-data text-[#116B6E]">
                {suppressions?.total ?? 0}
              </span>{" "}
              suppressed address{(suppressions?.total ?? 0) === 1 ? "" : "es"}
            </span>
            <span className="text-xs text-[#6F828A]">
              Updated live by Resend webhook
            </span>
          </div>
          {suppressions?.suppressions?.length ? (
            <ul className="divide-y divide-[#EEF3F4]" data-testid="admin-email-suppressions-list">
              {suppressions.suppressions.map((s) => (
                <li
                  key={s.email}
                  className="px-4 py-3 flex items-center gap-3"
                  data-testid={`admin-email-suppression-${s.email}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[#2E3A3F] font-mono-data truncate">{s.email}</div>
                    <div className="text-xs text-[#6F828A] mt-0.5 flex flex-wrap gap-x-3 gap-y-1">
                      <span>
                        Reason:{" "}
                        <span className="font-medium text-[#5C5C5C]">
                          {s.suppressed_reason === "complained"
                            ? "Spam complaint"
                            : s.suppressed_reason === "permanent_bounce"
                              ? "Permanent bounce"
                              : s.suppressed_reason === "threshold"
                                ? `${s.bounce_count}× bounced`
                                : "Suppressed"}
                        </span>
                      </span>
                      {s.last_bounced_at && (
                        <span>
                          Last bounce: {new Date(s.last_bounced_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    {s.last_bounce_message && (
                      <div className="text-xs text-[#8A8A8A] italic mt-0.5 truncate">
                        “{s.last_bounce_message}”
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await api.post("/admin/email-suppressions/reset", { email: s.email });
                        toast.success(`Reset ${s.email}`);
                        setSupBust((n) => n + 1);
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || "Reset failed");
                      }
                    }}
                    data-testid={`admin-email-suppression-reset-${s.email}`}
                    className="text-xs font-medium text-[#1F9FA4] hover:text-[#198185] hover:underline whitespace-nowrap"
                  >
                    Reset
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div
              className="px-4 py-8 text-center text-sm text-[#6F828A]"
              data-testid="admin-email-suppressions-empty"
            >
              No suppressed addresses. Your sender reputation is clean.
            </div>
          )}
        </div>
      </section>

      {/* v77.18 — Inactive accounts summary card. Free-tier-only
          lifecycle: 90d → email → 7d → final warning → 7d → archive
          → 30d → permanent anonymisation. Click through to /admin/inactive
          for per-row actions. */}
      <section className="space-y-3" data-testid="admin-inactivity-summary-section">
        <h2 className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium inline-flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5" /> Inactive free-tier accounts
        </h2>
        <div className="border border-[#D9E1E3] bg-white rounded-md p-4">
          {inactivitySummary ? (
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
              {[
                { k: "inactive_90_plus", label: "90+ days inactive", color: "text-[#8C2A22]" },
                { k: "warned", label: "Final warning", color: "text-[#8C2A22]" },
                { k: "archived", label: "Archived", color: "text-[#5C5C5C]" },
                { k: "scheduled_delete", label: "Pending delete", color: "text-[#C0291D]" },
                { k: "deleted", label: "Anonymised", color: "text-[#2E3A3F]" },
              ].map((c) => (
                <button
                  key={c.k}
                  type="button"
                  onClick={() => navigate(`/app/admin/inactive`)}
                  data-testid={`admin-inactivity-tile-${c.k}`}
                  className="text-left p-3 rounded-md hover:bg-[#F8FAFA] transition-colors"
                >
                  <div className={`text-2xl font-mono-data tabular-nums font-semibold ${c.color}`}>
                    {(inactivitySummary[c.k] ?? 0).toLocaleString()}
                  </div>
                  <div className="text-xs text-[#6F828A] mt-0.5">{c.label}</div>
                </button>
              ))}
            </div>
          ) : (
            <div className="text-sm text-[#6F828A]">Loading…</div>
          )}
          <div className="border-t border-[#EEF3F4] pt-3 mt-3 flex items-center justify-between">
            <p className="text-xs text-[#6F828A]">
              Free-tier only · Paid, complimentary, admin and protected accounts are exempt.
            </p>
            <button
              type="button"
              onClick={() => navigate("/app/admin/inactive")}
              data-testid="admin-inactivity-open-detail"
              className="text-xs font-medium text-[#1F9FA4] hover:text-[#198185] hover:underline"
            >
              Open detail →
            </button>
          </div>
        </div>
      </section>

      {/* Onboarding funnel */}
      <section className="space-y-3" data-testid="admin-onboarding-section">
        <h2 className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium">Onboarding funnel</h2>
        <div className="border border-[#D9E1E3] bg-white rounded-md p-4 space-y-3">
          {analytics?.onboarding_funnel?.length ? (
            analytics.onboarding_funnel.map((step) => {
              const denom = step.total || 1;
              const pct = Math.round((step.count / denom) * 100);
              return (
                <div key={step.label} className="text-sm" data-testid={`admin-onboarding-${step.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <div className="flex justify-between items-baseline">
                    <span className="text-[#2E3A3F]">
                      {step.label}
                      {step.is_window && <span className="ml-1.5 text-[10px] uppercase tracking-[0.08em] text-[#6F828A]">· last {range}d</span>}
                    </span>
                    <span className="font-mono-data tabular-nums text-[#6F828A]">
                      {step.count.toLocaleString()}
                      {!step.is_window && <span className="ml-1">/ {step.total.toLocaleString()} ({pct}%)</span>}
                    </span>
                  </div>
                  <div className="mt-1 h-2 rounded-sm bg-[#EEF3F4] overflow-hidden">
                    <div className="h-full bg-[#1F9FA4]" style={{ width: `${Math.max(2, Math.min(100, pct))}%` }} />
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-sm text-[#6F828A]">Loading…</div>
          )}
        </div>
        <p className="text-[11px] text-[#6F828A]">Cumulative household-level progression. &ldquo;Invited / accepted&rdquo; counts are within the selected time window.</p>
      </section>

      {/* PDF exports by type */}
      <section className="space-y-3" data-testid="admin-pdf-section">
        <h2 className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium">PDF exports by type · last {range} days</h2>
        <div className="border border-[#D9E1E3] bg-white rounded-md p-4 space-y-3">
          {analytics?.pdf_exports?.length ? (
            (() => {
              const maxPdf = Math.max(1, ...analytics.pdf_exports.map((p) => p.count));
              return analytics.pdf_exports.map((p) => (
                <BarRow key={p.label} label={p.label} count={p.count} max={maxPdf} testid={`admin-pdf-${p.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} />
              ));
            })()
          ) : (
            <div className="text-sm text-[#6F828A]">Loading…</div>
          )}
        </div>
      </section>

      {/* Account lifecycle + Push */}
      <section className="space-y-3" data-testid="admin-lifecycle-section">
        <h2 className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium">Account lifecycle · last {range} days</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {analytics?.account_lifecycle?.map((l) => (
            <div key={l.label} className="border border-[#D9E1E3] bg-white rounded-md p-3" data-testid={`admin-lifecycle-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">{l.label}</div>
              <div className="font-heading text-2xl font-semibold tabular-nums mt-1 text-[#2E3A3F]">{l.count.toLocaleString()}</div>
            </div>
          ))}
        </div>
        {analytics?.pwa && (
          <div className="text-xs text-[#6F828A] mt-2 flex flex-wrap gap-x-4 gap-y-1" data-testid="admin-push-summary">
            <span>Active push subscriptions: <span className="font-mono-data tabular-nums text-[#2E3A3F]">{analytics.pwa.active_push_subscriptions.toLocaleString()}</span></span>
          </div>
        )}
      </section>

      {/* PWA installs */}
      <section className="space-y-3" data-testid="admin-pwa-section">
        <h2 className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium">PWA installs · last {range} days</h2>
        <div className="border border-[#D9E1E3] bg-white rounded-md p-4">
          <div className="flex items-baseline gap-6 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">Total installs</div>
              <div className="font-heading text-3xl font-semibold tabular-nums text-[#2E3A3F]" data-testid="admin-pwa-total">
                {(analytics?.pwa?.installs_total ?? 0).toLocaleString()}
              </div>
            </div>
            <div className="flex-1 min-w-[200px] space-y-2">
              {analytics?.pwa?.installs_by_platform?.length ? (
                (() => {
                  const maxP = Math.max(1, ...analytics.pwa.installs_by_platform.map((p) => p.count));
                  return analytics.pwa.installs_by_platform.map((p) => (
                    <BarRow key={p.platform} label={p.platform} count={p.count} max={maxP} testid={`admin-pwa-platform-${p.platform}`} />
                  ));
                })()
              ) : (
                <div className="text-sm text-[#6F828A]">No installs recorded yet.</div>
              )}
            </div>
          </div>
          <p className="text-[11px] text-[#6F828A] mt-3 italic">Tracked via the browser&apos;s <code className="font-mono-data">appinstalled</code> event. Not every browser fires it — Safari / iOS counts will under-report.</p>
        </div>
      </section>

      {/* Technical health */}
      <section className="space-y-3" data-testid="admin-errors-section">
        <h2 className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium">Technical health · last {range} days</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="border border-[#D9E1E3] bg-white rounded-md p-4">
            <div className="flex items-baseline justify-between">
              <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">Server errors (5xx)</div>
              <div className="font-heading text-2xl font-semibold tabular-nums text-[#2E3A3F]" data-testid="admin-server-errors-count">
                {(analytics?.errors?.server_errors ?? 0).toLocaleString()}
              </div>
            </div>
            <div className="mt-3 space-y-1.5">
              {analytics?.errors?.top_server_errors?.length ? analytics.errors.top_server_errors.map((e, i) => (
                <div key={`${e.status}-${e.path}-${i}`} className="flex items-baseline justify-between text-xs border-b border-[#EEF3F4] last:border-0 pb-1">
                  <span className="font-mono-data text-[#2E3A3F] truncate max-w-[75%]" title={e.path}>{e.status} · {e.path}</span>
                  <span className="font-mono-data tabular-nums text-[#6F828A] ml-2">{e.count}</span>
                </div>
              )) : (
                <div className="text-sm text-[#6F828A]">No server errors in this range.</div>
              )}
            </div>
          </div>
          <div className="border border-[#D9E1E3] bg-white rounded-md p-4">
            <div className="flex items-baseline justify-between">
              <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">Client errors (browser)</div>
              <div className="font-heading text-2xl font-semibold tabular-nums text-[#2E3A3F]" data-testid="admin-client-errors-count">
                {(analytics?.errors?.client_errors ?? 0).toLocaleString()}
              </div>
            </div>
            <div className="mt-3 space-y-1.5">
              {analytics?.errors?.top_client_errors?.length ? analytics.errors.top_client_errors.map((e, i) => (
                <div key={`${e.message}-${i}`} className="flex items-baseline justify-between text-xs border-b border-[#EEF3F4] last:border-0 pb-1">
                  <span className="text-[#2E3A3F] truncate max-w-[75%]" title={e.message}>{e.message}</span>
                  <span className="font-mono-data tabular-nums text-[#6F828A] ml-2">{e.count}</span>
                </div>
              )) : (
                <div className="text-sm text-[#6F828A]">No client errors in this range.</div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Feature requests by category */}
      <section className="space-y-3" data-testid="admin-fr-category-section">
        <h2 className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium">Feature request volume by category</h2>
        <div className="border border-[#D9E1E3] bg-white rounded-md p-4 space-y-3">
          {analytics?.feature_requests_by_category?.length ? (
            (() => {
              const maxC = Math.max(1, ...analytics.feature_requests_by_category.map((c) => c.count));
              return analytics.feature_requests_by_category.map((c) => (
                <BarRow key={c.label} label={c.label} count={c.count} max={maxC} testid={`admin-fr-cat-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} />
              ));
            })()
          ) : (
            <div className="text-sm text-[#6F828A]">Loading…</div>
          )}
        </div>
        <p className="text-[11px] text-[#6F828A]">Cumulative — all submissions, regardless of date window. Use the Feature Requests section below to review individual items.</p>
      </section>

      <p className="text-[11px] text-[#6F828A] italic border-t border-[#D9E1E3] pt-4">
        Privacy note: these metrics are aggregated counts derived from the existing audit log and session index. No message bodies, expense amounts, journal entries, children&apos;s details or user identifiers are read or persisted by this dashboard.
      </p>

      {/* Feature Requests review — shows submitter identifiers by design, so it's below the privacy note */}
      <section className="space-y-3" data-testid="admin-feature-requests-section">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <h2 className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium inline-flex items-center gap-1.5">
            <Lightbulb className="w-3.5 h-3.5" /> Feature requests
          </h2>
          {frData && (
            <div className="text-xs text-[#6F828A] font-mono-data tabular-nums" data-testid="admin-fr-total">
              {frData.total_all?.toLocaleString?.() ?? 0} total submission{(frData.total_all ?? 0) === 1 ? "" : "s"}
            </div>
          )}
        </div>

        {/* Filter chips */}
        <div className="inline-flex flex-wrap border border-[#D9E1E3] rounded-sm overflow-hidden text-sm" role="radiogroup" aria-label="Filter feature requests by status" data-testid="admin-fr-filter">
          {[
            { key: "new", label: "New" },
            { key: "reviewed", label: "Reviewed" },
            { key: "planned", label: "Planned" },
            { key: "shipped", label: "Shipped" },
            { key: "declined", label: "Declined" },
            { key: "all", label: "All" },
          ].map((opt) => {
            const count = opt.key === "all" ? (frData?.total_all ?? 0) : (frData?.counts?.[opt.key] ?? 0);
            const active = frStatus === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => setFrStatus(opt.key)}
                className={`px-3 py-1.5 transition-colors border-r border-[#D9E1E3] last:border-r-0 ${active ? "bg-[#1F9FA4] text-white" : "bg-white text-[#6F828A] hover:bg-[#EEF3F4]"}`}
                data-testid={`admin-fr-filter-${opt.key}`}
              >
                {opt.label}
                <span className={`ml-1.5 font-mono-data tabular-nums ${active ? "text-white/80" : "text-[#6F828A]"}`}>{count}</span>
              </button>
            );
          })}
        </div>

        <div className="border border-[#D9E1E3] bg-white rounded-md divide-y divide-[#D9E1E3]" data-testid="admin-fr-list">
          {frBusy && !frData && (
            <div className="p-6 text-center text-sm text-[#6F828A]">Loading requests…</div>
          )}
          {frData && frData.requests.length === 0 && (
            <div className="p-6 text-center text-sm text-[#6F828A]" data-testid="admin-fr-empty">
              No requests with this status.
            </div>
          )}
          {frData?.requests?.map((r) => (
            <div key={r.id} className="p-4" data-testid={`admin-fr-row-${r.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-[#2E3A3F]">{r.title}</span>
                    <StatusBadge status={r.status} />
                    {r.category && (
                      <span className="text-[10px] uppercase tracking-[0.08em] px-2 py-0.5 bg-[#F4F7F7] text-[#6F828A] rounded-sm">{r.category}</span>
                    )}
                    {r.priority && (
                      <span className="text-[10px] uppercase tracking-[0.08em] px-2 py-0.5 bg-[#FDF4E2] text-[#8F6B1F] rounded-sm">{r.priority.replace(/_/g, " ")}</span>
                    )}
                  </div>
                  <div className="text-sm text-[#2E3A3F] mt-1.5 whitespace-pre-wrap">{r.description}</div>
                  {r.admin_note && (
                    <div className="mt-2 text-xs text-[#4A6170] bg-[#F4F7F7] border-l-2 border-[#1F9FA4] rounded-sm px-3 py-2">
                      <span className="uppercase tracking-[0.08em] text-[10px] text-[#6F828A] font-semibold">Internal note</span>
                      <div className="mt-0.5 whitespace-pre-wrap">{r.admin_note}</div>
                    </div>
                  )}
                  <div className="text-[11px] text-[#6F828A] font-mono-data mt-2 flex flex-wrap gap-x-3 gap-y-1">
                    <span>{r.user_name || "Anonymous"}{r.user_email ? ` · ${r.user_email}` : ""}</span>
                    <span>Submitted {formatDT(r.created_at)}</span>
                    {r.app_version && <span>v{r.app_version}</span>}
                    {r.reviewed_at && <span>Reviewed {formatDT(r.reviewed_at)}</span>}
                  </div>
                </div>
                <div className="flex flex-col gap-2 flex-shrink-0">
                  {r.status === "new" && (
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => markReviewed(r)}
                      className="bg-[#1F9FA4] hover:bg-[#198185] text-white rounded-sm"
                      data-testid={`admin-fr-review-${r.id}`}
                    >
                      Mark reviewed
                    </Button>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => openEdit(r)}
                    className="border-[#D9E1E3] text-[#2E3A3F] hover:bg-[#EEF3F4] rounded-sm"
                    data-testid={`admin-fr-edit-${r.id}`}
                  >
                    Update status
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* v77.43 — Bug reports admin section */}
      <section className="space-y-3" data-testid="admin-bug-reports-section">
        <div className="flex items-end justify-between gap-2 flex-wrap">
          <h2 className="font-heading text-lg sm:text-xl font-medium text-[#2E3A3F] inline-flex items-center gap-2">
            <Bug className="w-3.5 h-3.5" /> Bug reports
          </h2>
          {brData && (
            <div className="text-xs text-[#6F828A]">
              {brData.total_all?.toLocaleString?.() ?? 0} total report{(brData.total_all ?? 0) === 1 ? "" : "s"}
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            { key: "new", label: "New" },
            { key: "triaged", label: "Triaged" },
            { key: "fixed", label: "Fixed" },
            { key: "wontfix", label: "Won't fix" },
            { key: "all", label: "All" },
          ].map((opt) => {
            const count = opt.key === "all" ? (brData?.total_all ?? 0) : (brData?.counts?.[opt.key] ?? 0);
            const active = brStatus === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => setBrStatus(opt.key)}
                className={`text-xs rounded-sm border px-2.5 py-1 transition-colors ${
                  active
                    ? "bg-[#1F9FA4] border-[#1F9FA4] text-white"
                    : "bg-white border-[#D9E1E3] text-[#2E3A3F] hover:bg-[#EEF3F4]"
                }`}
                data-testid={`admin-br-filter-${opt.key}`}
              >
                {opt.label} <span className="opacity-80 ml-0.5">({count})</span>
              </button>
            );
          })}
        </div>

        <div className="space-y-3">
          {brBusy && !brData && (
            <div className="text-sm text-[#6F828A]">Loading bug reports…</div>
          )}
          {brData && brData.reports.length === 0 && (
            <div className="border border-[#D9E1E3] bg-white rounded-md p-4 text-sm text-[#6F828A]" data-testid="admin-br-empty">
              No bug reports in this view.
            </div>
          )}
          {brData?.reports?.map((r) => (
            <div key={r.id} className="border border-[#D9E1E3] bg-white rounded-md p-4 space-y-2" data-testid={`admin-br-row-${r.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-[0.08em] text-[#6F828A]">
                    {r.user_role} · {r.user_email || "—"} · {r.user_name || "—"}
                  </div>
                  <div className="font-medium text-[#2E3A3F] mt-0.5 break-words">
                    {r.description}
                  </div>
                  {r.intent && (
                    <div className="text-sm text-[#6F828A] mt-1">
                      <span className="text-xs uppercase tracking-[0.08em]">Intent: </span>
                      {r.intent}
                    </div>
                  )}
                </div>
                <span
                  className={`inline-flex items-center text-[10px] uppercase tracking-[0.08em] font-semibold rounded-sm px-1.5 py-0.5 flex-shrink-0 ${
                    r.status === "fixed" ? "text-[#116B6E] bg-[#D6F0EF] border border-[#B4E0DE]"
                    : r.status === "wontfix" ? "text-[#6F828A] bg-[#EEF3F4] border border-[#D9E1E3]"
                    : r.status === "triaged" ? "text-[#5C3A1E] bg-[#FBF0DC] border border-[#E8C29A]"
                    : "text-[#8C2A22] bg-[#FBEFED] border border-[#F0BFB6]"
                  }`}
                >
                  {r.status}
                </span>
              </div>
              <div className="text-xs text-[#6F828A] flex flex-wrap gap-x-3 gap-y-1 font-mono-data">
                <span>route: {r.route || "—"}</span>
                <span>scope: {r.is_all_children_view ? "all-children" : (r.selected_child_id ? `child:${r.selected_child_id.slice(0, 8)}…` : "—")}</span>
                <span>viewport: {r.viewport || "—"}</span>
                <span>{formatDT(r.created_at)}</span>
              </div>
              {r.admin_note && (
                <div className="text-xs text-[#5C5C5C] bg-[#FAFBFB] border border-[#EEF3F4] rounded-sm px-2 py-1.5">
                  <span className="uppercase tracking-[0.08em] text-[#6F828A]">Note: </span>
                  {r.admin_note}
                </div>
              )}
              <div className="flex justify-end gap-2 pt-1">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setBrEditing(r);
                    setBrStatusDraft(r.status || "new");
                    setBrNoteDraft(r.admin_note || "");
                  }}
                  className="border-[#D9E1E3] text-[#2E3A3F] hover:bg-[#EEF3F4] rounded-sm"
                  data-testid={`admin-br-edit-${r.id}`}
                >
                  Update status
                </Button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <Dialog open={!!brEditing} onOpenChange={(v) => !v && setBrEditing(null)}>
        <DialogContent preventOutsideClose className="bg-white border-[#D9E1E3] max-w-lg" data-testid="admin-br-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Update bug report</DialogTitle>
            <DialogDescription className="text-[#2E3A3F]">{brEditing?.description?.slice(0, 120)}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div>
              <div className="text-xs uppercase tracking-[0.08em] text-[#6F828A] mb-1">Status</div>
              <Select value={brStatusDraft} onValueChange={setBrStatusDraft}>
                <SelectTrigger data-testid="admin-br-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="triaged">Triaged</SelectItem>
                  <SelectItem value="fixed">Fixed</SelectItem>
                  <SelectItem value="wontfix">Won&apos;t fix</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.08em] text-[#6F828A] mb-1">Internal note <span className="normal-case tracking-normal">(not shown to users)</span></div>
              <Textarea
                value={brNoteDraft}
                onChange={(e) => setBrNoteDraft(e.target.value)}
                rows={4}
                maxLength={4000}
                placeholder="Repro steps, related ticket, target release…"
                data-testid="admin-br-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setBrEditing(null)} disabled={brSaveBusy} data-testid="admin-br-cancel">Cancel</Button>
            <Button
              type="button"
              onClick={async () => {
                if (!brEditing) return;
                setBrSaveBusy(true);
                try {
                  await api.patch(`/admin/bug-reports/${brEditing.id}`, { status: brStatusDraft, admin_note: brNoteDraft });
                  toast.success("Saved");
                  setBrEditing(null);
                  // refresh
                  const params = brStatus === "all" ? "" : `?status=${encodeURIComponent(brStatus)}`;
                  const { data } = await api.get(`/admin/bug-reports${params}`);
                  setBrData(data);
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Couldn't update");
                } finally {
                  setBrSaveBusy(false);
                }
              }}
              disabled={brSaveBusy}
              className="bg-[#1F9FA4] hover:bg-[#198185] text-white rounded-sm"
              data-testid="admin-br-save"
            >
              {brSaveBusy ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!frEditing} onOpenChange={(v) => !v && closeEdit()}>
        <DialogContent preventOutsideClose className="bg-white border-[#D9E1E3] max-w-lg" data-testid="admin-fr-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Update feature request</DialogTitle>
            <DialogDescription className="text-[#2E3A3F]">
              {frEditing?.title}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div>
              <div className="text-xs uppercase tracking-[0.08em] text-[#6F828A] mb-1">Status</div>
              <Select value={frStatusDraft} onValueChange={setFrStatusDraft}>
                <SelectTrigger data-testid="admin-fr-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="reviewed">Reviewed</SelectItem>
                  <SelectItem value="planned">Planned</SelectItem>
                  <SelectItem value="shipped">Shipped</SelectItem>
                  <SelectItem value="declined">Declined</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.08em] text-[#6F828A] mb-1">Internal note <span className="normal-case tracking-normal">(not shown to users)</span></div>
              <Textarea
                value={frNoteDraft}
                onChange={(e) => setFrNoteDraft(e.target.value)}
                rows={4}
                maxLength={2000}
                placeholder="Rationale, related ticket, target release…"
                data-testid="admin-fr-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={closeEdit} disabled={frSaveBusy} data-testid="admin-fr-cancel">Cancel</Button>
            <Button
              type="button"
              onClick={saveEdit}
              disabled={frSaveBusy}
              className="bg-[#1F9FA4] hover:bg-[#198185] text-white rounded-sm"
              data-testid="admin-fr-save"
            >
              {frSaveBusy ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Status pill. Colours match the admin palette — neutral teal for "reviewed", sand for "planned",
// etc. Kept lightweight and dependency-free.
const StatusBadge = ({ status }) => {
  const map = {
    new:      { bg: "bg-[#FDF4E2]", fg: "text-[#8F6B1F]", border: "border-[#E6CFA2]", label: "New" },
    reviewed: { bg: "bg-[#E0F2F2]", fg: "text-[#116B6E]", border: "border-[#A7D8DA]", label: "Reviewed" },
    planned:  { bg: "bg-[#ECE3F3]", fg: "text-[#4E3877]", border: "border-[#C5B2DE]", label: "Planned" },
    shipped:  { bg: "bg-[#DCEFDC]", fg: "text-[#1E5A1E]", border: "border-[#A9D3A9]", label: "Shipped" },
    declined: { bg: "bg-[#F0EFEE]", fg: "text-[#6F828A]", border: "border-[#CFD6D8]", label: "Declined" },
  };
  const s = map[status] || map.new;
  return (
    <span className={`text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 rounded-sm border ${s.bg} ${s.fg} ${s.border}`} data-testid={`admin-fr-status-${status}`}>
      {s.label}
    </span>
  );
};
